/**
 * AuthContext — manages authentication state for the portal.
 *
 * On mount, fetches /api/auth/config and /api/me to determine:
 *   - whether dev-login is enabled
 *   - whether the user is already logged in (session cookie)
 *
 * Exposes: user, uiPermissions, devicePermissions, roles, login(), logout(),
 *          isAuthenticated, devLoginEnabled, loading.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);          // /api/me response or null
  const [devLoginEnabled, setDevLoginEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch config + session on mount (use AbortController to prevent React StrictMode double-fetch)
  useEffect(() => {
    const abortController = new AbortController();
    (async () => {
      try {
        const config = await authService.getConfig(abortController.signal);
        if (!abortController.signal.aborted) setDevLoginEnabled(!!config.devLoginEnabled);
      } catch {
        // backend not reachable — leave devLoginEnabled as false
      }

      try {
        const me = await authService.getMe(abortController.signal);
        if (!abortController.signal.aborted && me && me.email) setUser(me);
      } catch {
        // not authenticated — leave user null
      }

      if (!abortController.signal.aborted) setLoading(false);
    })();
    return () => { abortController.abort(); };
  }, []);

  const login = useCallback(async (identifier) => {
    const result = await authService.devLogin(identifier);
    // After login, fetch full user profile
    const me = await authService.getMe();
    setUser(me);
    return result;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch { /* ignore */ }
    setUser(null);
  }, []);

  const isAuthenticated = !!user;

  const uiPermissions = useMemo(() => user?.uiPermissions || {}, [user]);
  const devicePermissions = useMemo(() => user?.devicePermissions || {}, [user]);
  const roles = useMemo(() => user?.roles || [], [user]);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    devLoginEnabled,
    loading,
    uiPermissions,
    devicePermissions,
    roles,
    login,
    logout,
  }), [user, isAuthenticated, devLoginEnabled, loading, uiPermissions, devicePermissions, roles, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export default AuthContext;

